import { CrmDatabase } from '../db/database';
import { RootStore } from '../../stores/RootStore';
import { SignalRClient } from './SignalRClient';
import { networkStatus } from '../network/status';
import { isNetworkError } from '../api/errors';
import type { EntityType } from '../../stores/types';
import { toast } from 'sonner';

interface SyncBootstrapResponse {
  data: Array<Record<string, unknown> & { _entity?: string }>;
  currentVersion: number;
  nextCursor: string | null;
  isComplete: boolean;
}

interface SyncDeltaResponse {
  changes: SyncChange[];
  currentVersion: number;
  hasMore: boolean;
}

interface SyncChange {
  entity: string;
  operation: string;
  id: string;
  data?: Record<string, unknown>;
  syncVersion: number;
  isDeleted: boolean;
}

interface SyncPushResponse {
  results: Array<{ id: string; success: boolean; serverVersion: number; error?: string }>;
  currentVersion: number;
}

// When running inside Capacitor the WebView origin is `https://localhost` and relative fetches
// resolve against the APK filesystem (returning index.html → doctype JSON parse errors).
// `VITE_API_URL` is set to the production origin in `build:cap`, so prepend it unconditionally.
const API_BASE = import.meta.env.VITE_API_URL ?? '';

const ENTITY_DB_STORE: Record<string, 'contacts' | 'deals' | 'dealTypes' | 'projects' | 'tasks' | 'notes' | 'noteAttachments' | 'auditLogs' | 'users'> = {
  contact: 'contacts',
  deal: 'deals',
  dealType: 'dealTypes',
  project: 'projects',
  task: 'tasks',
  note: 'notes',
  noteAttachment: 'noteAttachments',
  auditLog: 'auditLogs',
  user: 'users',
};

export class SyncEngine {
  private db: CrmDatabase;
  private store: RootStore;
  private signalR: SignalRClient;
  private deltaTimer: ReturnType<typeof setTimeout> | null = null;
  private deltaFailures = 0;
  private pushInterval: ReturnType<typeof setInterval> | null = null;
  private token: string;
  private disposed = false;
  private _fetchingDelta = false;
  private _deltaPending = false;
  // Callback invoked after server changes are applied to MobX/IDB. Used by SyncProvider
  // to invalidate React Query caches (notes attachments, activity logs) so they refetch
  // fresh data instead of showing stale persisted entries until the 5-min staleTime expires.
  private onChangesApplied?: (entityTypes: Set<string>) => void;

  constructor(
    db: CrmDatabase,
    store: RootStore,
    token: string,
    onChangesApplied?: (entityTypes: Set<string>) => void,
  ) {
    this.db = db;
    this.store = store;
    this.token = token;
    this.onChangesApplied = onChangesApplied;
    this.signalR = new SignalRClient(
      token,
      this.handleSignalRDelta.bind(this),
      () => this.fetchDelta(),
    );
  }

  async initialize(): Promise<void> {
    this.setupNetworkListeners();
    try {
      await this.store.loadFromDatabase(this.db);
    } catch (err) {
      // In React StrictMode the effect cleanup fires while this await is in
      // flight, closing the DB before loadFromDatabase resolves. Swallow the
      // error silently — the second effect invocation will re-initialize cleanly.
      if (this.disposed) return;
      throw err;
    }
    if (this.disposed) return;

    if (this.store.syncStore.serverBootstrapDone) {
      this.startDeltaSync();
      this.startPushSync();
      // Connect SignalR in the background — don't block UI rendering
      this.signalR.connect().catch(() => {});
    }
  }

  async bootstrap(): Promise<void> {
    if (this.disposed) return;
    this.store.syncStore.setStatus('bootstrapping');

    try {
      let cursor: string | null = null;
      let isComplete = false;
      let isFirstPage = true;

      while (!isComplete && !this.disposed) {
        const response = await this.fetchBootstrap(cursor);
        await this.processBootstrapData(response.data);

        // After the first page (init phase: dealTypes, users, projects),
        // the UI can render — dropdowns and lookups are ready.
        if (isFirstPage) {
          this.store.syncStore.setMetadataReady(true);
          isFirstPage = false;
        }

        cursor = response.nextCursor;
        isComplete = response.isComplete;

        if (response.currentVersion > 0) {
          this.store.syncStore.setSyncVersion(response.currentVersion);
        }
      }

      this.store.syncStore.setBootstrapComplete(true);
      this.store.syncStore.serverBootstrapDone = true;
      await this.db.setSyncMeta({
        lastSyncVersion: this.store.syncStore.lastSyncVersion,
        lastSyncAt: new Date().toISOString(),
        bootstrapComplete: true,
      });

      this.store.syncStore.setStatus('idle');

      this.startDeltaSync();
      this.startPushSync();
      await this.signalR.connect();
    } catch (error) {
      this.store.syncStore.setError(error instanceof Error ? error.message : 'Bootstrap failed');
      throw error;
    }
  }

  private async processBootstrapData(data: Array<Record<string, unknown>>): Promise<void> {
    const grouped = new Map<string, Record<string, unknown>[]>();

    for (const item of data) {
      const entityType = (item._entity as string) || 'unknown';
      if (!grouped.has(entityType)) grouped.set(entityType, []);

      const cleanItem = { ...item };
      delete cleanItem._entity;
      grouped.get(entityType)!.push(cleanItem);
    }

    for (const [entityType, items] of grouped) {
      const storeName = ENTITY_DB_STORE[entityType];
      if (!storeName) continue;

      if (storeName === 'users') {
        await this.db.putMany('users', items);
        this.store.userStore.loadFromRecords(items);
      } else {
        await this.db.putMany(storeName, items);
        const entityStore = this.store.getStoreForEntity(entityType as EntityType);
        if (entityStore && 'loadFromRecords' in entityStore) {
          (entityStore as { loadFromRecords: (records: Record<string, unknown>[]) => void }).loadFromRecords(items);
        }
      }
    }
  }

  startDeltaSync(): void {
    if (this.deltaTimer !== null) return;
    // Poll as a SignalR fallback / heartbeat. Interval grows on consecutive
    // failures so an API outage doesn't flood the console with 502s.
    void this.fetchDelta();
    this.scheduleNextDelta();
  }

  private scheduleNextDelta(): void {
    if (this.disposed || this.deltaTimer !== null) return;
    const delay = this.nextDeltaDelay();
    this.deltaTimer = setTimeout(async () => {
      this.deltaTimer = null;
      // When SignalR is alive, deltas arrive via push + onreconnected.
      // Skip the network/IDB round-trip and just keep the heartbeat timer alive
      // so we resume polling immediately if the socket drops.
      if (!this.signalR.isConnected) {
        await this.fetchDelta();
      }
      this.scheduleNextDelta();
    }, delay);
  }

  private nextDeltaDelay(): number {
    switch (Math.min(this.deltaFailures, 3)) {
      case 0: return 30_000;
      case 1: return 60_000;
      case 2: return 120_000;
      default: return 300_000;
    }
  }

  private stopDeltaSync(): void {
    if (this.deltaTimer) {
      clearTimeout(this.deltaTimer);
      this.deltaTimer = null;
    }
  }

  startPushSync(): void {
    if (this.pushInterval) return;
    this.pushInterval = setInterval(() => this.flushPushQueue(), 2000);
    this.store.syncStore.setFlushFn(() => this.flushPushQueue());
  }

  private stopPushSync(): void {
    if (this.pushInterval) {
      clearInterval(this.pushInterval);
      this.pushInterval = null;
    }
  }

  private async fetchBootstrap(cursor: string | null): Promise<SyncBootstrapResponse> {
    const response = await fetch(`${API_BASE}/api/sync/bootstrap`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`,
      },
      body: JSON.stringify({ cursor }),
    });

    if (!response.ok) throw new Error(`Bootstrap failed: ${response.status}`);
    return response.json();
  }

  async fetchDelta(): Promise<void> {
    if (this.disposed || this.store.syncStore.status === 'bootstrapping') return;
    if (networkStatus.status === 'offline') return;

    // Prevent concurrent fetches; queue at most one pending retry
    if (this._fetchingDelta) {
      this._deltaPending = true;
      return;
    }

    this._fetchingDelta = true;
    try {
      this.store.syncStore.setStatus('syncing');
      const since = this.store.syncStore.lastSyncVersion;

      const response = await fetch(`${API_BASE}/api/sync/delta?since=${since}&limit=1000`, {
        headers: { 'Authorization': `Bearer ${this.token}` },
      });

      if (!response.ok) {
        const err = new Error(`Delta sync failed: ${response.status}`);
        (err as Error & { status?: number }).status = response.status;
        throw err;
      }

      const data: SyncDeltaResponse = await response.json();

      if (data.changes.length > 0) {
        await this.applyChanges(data.changes);
        this.store.syncStore.setSyncVersion(data.currentVersion);
        await this.db.setSyncMeta({
          lastSyncVersion: data.currentVersion,
          lastSyncAt: new Date().toISOString(),
          bootstrapComplete: true,
        });
      }

      this.deltaFailures = 0;
      networkStatus.recordSuccess();
      this.store.syncStore.setStatus('idle');

      if (data.hasMore) {
        await this.fetchDelta();
      }
    } catch (error) {
      if (isNetworkError(error)) {
        this.deltaFailures++;
        networkStatus.recordFailure();
        const current: string = networkStatus.status;
        this.store.syncStore.setStatus(current === 'offline' ? 'offline' : 'idle');
      } else {
        this.store.syncStore.setError(error instanceof Error ? error.message : 'Delta sync failed');
      }
    } finally {
      this._fetchingDelta = false;
      if (this._deltaPending && !this.disposed) {
        this._deltaPending = false;
        void this.fetchDelta();
      }
    }
  }

  async flushPushQueue(): Promise<void> {
    if (this.disposed) return;
    // Pause on offline / degraded — pushing now would burn the 5-retry budget for
    // each queued mutation against a server that's clearly not responding.
    if (networkStatus.status !== 'online') return;
    // In-memory gate: the 2s push interval runs constantly. When the outbox is
    // empty (the common case) skip the IDB read entirely — pendingMutationCount
    // is hydrated from IDB on load and kept accurate by queue/decrement calls.
    if (this.store.syncStore.pendingMutationCount === 0) return;

    const mutations = await this.db.getAllPendingMutations();
    if (mutations.length === 0) return;

    try {
      this.store.syncStore.setStatus('pushing');

      const response = await fetch(`${API_BASE}/api/sync/push`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.token}`,
        },
        body: JSON.stringify({
          mutations: mutations.map(m => ({
            entity: m.entity,
            operation: m.operation,
            id: m.entityId,
            data: m.data,
            clientTimestamp: m.clientTimestamp,
          })),
        }),
      });

      if (!response.ok) {
        const err = new Error(`Push failed: ${response.status}`);
        (err as Error & { status?: number }).status = response.status;
        throw err;
      }

      const data: SyncPushResponse = await response.json();

      // Safety: ensure result count matches
      const resultCount = Math.min(data.results.length, mutations.length);
      let removedCount = 0;
      for (let i = 0; i < resultCount; i++) {
        if (data.results[i].success) {
          await this.db.removePendingMutation(mutations[i].id);
          removedCount++;
        } else {
          const mutation = mutations[i];
          mutation.retryCount++;
          if (mutation.retryCount > 5) {
            await this.db.removePendingMutation(mutation.id);
            removedCount++;
            await this.rollbackMutation(mutation);
            console.error(
              `Mutation ${mutation.id} failed after 5 retries: entity=${mutation.entity}, op=${mutation.operation}, entityId=${mutation.entityId}`,
              data.results[i].error
            );
          } else {
            await this.db.addPendingMutation(mutation);
          }
        }
      }

      this.store.syncStore.decrementPendingCount(removedCount);
      this.store.syncStore.setStatus('idle');

      // Immediately pull delta after a successful push to pick up server-generated
      // audit logs and any other side effects (e.g. task created → activity entry).
      if (removedCount > 0) {
        void this.fetchDelta();
      }
    } catch (error) {
      if (isNetworkError(error)) {
        networkStatus.recordFailure();
        const current: string = networkStatus.status;
        this.store.syncStore.setStatus(current === 'offline' ? 'offline' : 'idle');
      } else {
        this.store.syncStore.setError(error instanceof Error ? error.message : 'Push failed');
      }
    }
  }

  private async applyChanges(changes: SyncChange[]): Promise<void> {
    // Skip changes for entities with pending local mutations (local wins until push resolves).
    // Gated on in-memory count so we don't hit IDB on every delta when the outbox is empty
    // (the common case).
    let pendingEntityIds: Set<string> | null = null;
    if (this.store.syncStore.pendingMutationCount > 0) {
      const pendingMutations = await this.db.getAllPendingMutations();
      pendingEntityIds = new Set(pendingMutations.map(m => m.entityId));
    }

    const grouped = new Map<string, SyncChange[]>();
    for (const change of changes) {
      if (!grouped.has(change.entity)) grouped.set(change.entity, []);
      grouped.get(change.entity)!.push(change);
    }

    const appliedEntityTypes = new Set<string>();

    for (const [entityType, entityChanges] of grouped) {
      const storeName = ENTITY_DB_STORE[entityType];
      if (!storeName) continue;

      const nonConflicting = pendingEntityIds
        ? entityChanges.filter(c => !pendingEntityIds!.has(c.id))
        : entityChanges;

      if (nonConflicting.length === 0) continue;

      // Users have no MobX entity store/applyChanges contract — bootstrap and
      // delta both go through putMany + UserStore.loadFromRecords.
      if (entityType === 'user') {
        const toPut = nonConflicting
          .filter(c => !c.isDeleted && c.data)
          .map(c => ({ ...c.data!, id: c.id }));
        const toDelete = nonConflicting.filter(c => c.isDeleted).map(c => c.id);

        await this.db.putMany('users', toPut);
        for (const id of toDelete) await this.db.delete('users', id);
        this.store.userStore.loadFromRecords(toPut);
        this.store.userStore.removeFromMap(toDelete);

        appliedEntityTypes.add('user');
        continue;
      }

      // Batch per-store writes into a single IDB transaction instead of one
      // transaction per change. For large deltas (post-outage catch-up) this
      // drops N round-trips to 1.
      const toPut: Record<string, unknown>[] = [];
      const toDelete: string[] = [];
      for (const change of nonConflicting) {
        if (change.isDeleted) {
          toDelete.push(change.id);
        } else if (change.data) {
          toPut.push({ ...change.data, id: change.id });
        }
      }

      await this.db.applyChangesToStore(storeName, toPut, toDelete);

      appliedEntityTypes.add(entityType);
      const entityStore = this.store.getStoreForEntity(entityType as EntityType);
      if (entityStore && 'applyChanges' in entityStore) {
        (entityStore as { applyChanges: (changes: SyncChange[]) => void }).applyChanges(nonConflicting);
      }
    }

    if (appliedEntityTypes.size > 0) {
      this.onChangesApplied?.(appliedEntityTypes);
    }
  }

  private async rollbackMutation(mutation: { entity: string; operation: string; entityId: string; data?: Record<string, unknown> }): Promise<void> {
    const storeName = ENTITY_DB_STORE[mutation.entity];
    if (!storeName || storeName === 'users') return;

    const entityStore = this.store.getStoreForEntity(mutation.entity as EntityType);

    try {
      if (mutation.operation === 'create') {
        // Entity never existed on server — remove from local state
        await this.db.delete(storeName, mutation.entityId);
        if (entityStore && 'removeFromMap' in entityStore) {
          (entityStore as { removeFromMap: (id: string) => void }).removeFromMap(mutation.entityId);
        }
        toast.error(`Failed to create ${mutation.entity}. It has been removed.`);
      } else if (mutation.operation === 'delete') {
        // Server rejected delete — restore locally
        const record = await this.db.get(storeName, mutation.entityId);
        if (record) {
          const restored = { ...record, isDeleted: false };
          await this.db.put(storeName, restored);
          if (entityStore && 'loadFromRecords' in entityStore) {
            (entityStore as { loadFromRecords: (records: Record<string, unknown>[]) => void }).loadFromRecords([restored]);
          }
        }
        toast.error(`Failed to delete ${mutation.entity}. It has been restored.`);
      } else if (mutation.operation === 'update') {
        // Force a delta sync to get correct server state
        toast.error(`Failed to update ${mutation.entity}. Refreshing data...`);
        await this.fetchDelta();
      }
    } catch (rollbackError) {
      console.error('Rollback failed:', rollbackError);
    }
  }

  private handleSignalRDelta(_notification: { entity: string; operation: string; id: string; orgId: string }): void {
    this.fetchDelta();
  }

  updateToken(token: string): void {
    this.token = token;
    this.signalR.updateToken(token);
  }

  private unsubscribeNetworkStatus: (() => void) | null = null;

  // Network connectivity handling
  private handleOnline = (): void => {
    if (this.store.syncStore.status === 'offline') {
      this.store.syncStore.setStatus('idle');
      this.deltaFailures = 0;

      if (!this.store.syncStore.serverBootstrapDone) {
        // Never bootstrapped — do the full initial sync now that we have connectivity.
        // bootstrap() calls startDeltaSync/startPushSync/signalR.connect on success.
        this.bootstrap().catch(() => {});
        return;
      }

      void this.fetchDelta();
      this.startDeltaSync();
      this.startPushSync();
      this.signalR.resume();
    }
  };

  private handleOffline = (): void => {
    this.store.syncStore.setStatus('offline');
    this.stopDeltaSync();
    this.stopPushSync();
  };

  // React to passive status store: when a back-to-online transition fires
  // (next fetchDelta success after an outage), flush the outbox immediately
  // instead of waiting for the 2s push interval tick.
  private handleNetworkStatusChange = (status: 'online' | 'degraded' | 'offline'): void => {
    if (status === 'online' && !this.disposed) {
      this.deltaFailures = 0;
      if (this.pushInterval === null && this.store.syncStore.bootstrapComplete) {
        this.startPushSync();
      }
      void this.flushPushQueue();
    }
  };

  private handleVisibilityChange = (): void => {
    if (document.visibilityState !== 'visible') return;
    if (this.disposed || !this.store.syncStore.bootstrapComplete) return;

    // SignalRClient self-heals via scheduleReconnect; nudge it immediately on tab focus
    if (!this.signalR.isConnected) {
      this.signalR.resume();
    }

    this.startDeltaSync();
    this.startPushSync();
    this.fetchDelta();
  };

  private setupNetworkListeners(): void {
    if (typeof window === 'undefined') return;
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.unsubscribeNetworkStatus = networkStatus.subscribe(this.handleNetworkStatusChange);
    if (!navigator.onLine) {
      this.store.syncStore.setStatus('offline');
    }
  }

  private removeNetworkListeners(): void {
    if (typeof window === 'undefined') return;
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.unsubscribeNetworkStatus?.();
    this.unsubscribeNetworkStatus = null;
  }

  async dispose(): Promise<void> {
    this.disposed = true;
    this.removeNetworkListeners();
    this.stopDeltaSync();
    this.stopPushSync();
    await this.signalR.disconnect();
  }
}
